import React, { useState } from 'react';
import { Trash2, RotateCcw } from 'lucide-react';
import { decodeHtml } from '../utils/decodeHtml';
import { productPlaceholder } from '../utils/productPlaceholder';
import StatusBadge from './ui/StatusBadge';
import FichaBarra from './FichaBarra';
import './CatalogGrid.css';

const PLACEHOLDER = productPlaceholder(220, 160);

// Vista MOSAICO — co-titular de la tabla (ESTANDAR §9: exploración = tarjeta).
// F4: la tarjeta ya NO repite acciones (Editar/QR/Publicar viven en el
// inspector, brief §13.1). Clic en la tarjeta = abre el inspector, igual que
// clic en una fila de la tabla.

function stockState(p, threshold) {
  const out = (p.variations?.length > 0)
    ? p.variations.some((v) => (v.stock_quantity || 0) === 0)
    : (p.stock_quantity === 0 || p.stock_status === 'outofstock');
  if (out) return { variant: 'error', label: 'Agotado' };
  const low = (p.variations?.length > 0)
    ? p.variations.some((v) => v.stock_quantity > 0 && v.stock_quantity <= threshold)
    : (p.stock_quantity > 0 && p.stock_quantity <= threshold);
  if (low) return { variant: 'warning', label: 'Stock bajo' };
  const qty = (p.variations?.length > 0)
    ? p.variations.reduce((s, v) => s + (v.stock_quantity || 0), 0)
    : (p.stock_quantity || 0);
  return { variant: 'success', label: `${qty} und` };
}

function ProductCard({ product, onSelect, onDelete, onRestore, onDeleteForever, selected, threshold, prioridad = false }) {
  const [imgSrc, setImgSrc] = useState(product.image || PLACEHOLDER);
  // P3-9b (mosaico): srcset (medium_large 768 + variantes) para no pixelar en 2x;
  // se limpia si la imagen falla (cae al placeholder). Lazy salvo el primer bloque.
  const [imgSrcSet, setImgSrcSet] = useState(product.imageSrcset || '');
  // Tanda 3b (defecto 1c): los dos tipos de borrador se distinguen también en
  // la vista de tarjetas ("Borrador en WooCommerce" / "Borrador local").
  const isDraft = product.status === 'draft' || product.status === 'local';
  const name = decodeHtml(product.name);
  const st = stockState(product, threshold);
  const hasVars = product.variations?.length > 0;
  // 3e (ajuste 4): la caneca al hover exige anidar un botón, y un <button> no
  // puede contener otro — la tarjeta pasa a div[role=button] (mismo rol y
  // aria-label, Enter/Espacio la activan). Un borrador local no está en Woo:
  // sin caneca. Tanda 4: una tarjeta EN PAPELERA no abre inspector — muestra
  // Restaurar / Eliminar definitivamente, siempre visibles.
  const enPapelera = !!product.isTrash;
  const conCaneca = onDelete && !product.isLocalDraft && !enPapelera;

  return (
    <div
      role="button"
      tabIndex={0}
      className={`prod-card${isDraft ? ' prod-card--draft' : ''}${selected ? ' prod-card--selected' : ''}`}
      onClick={() => onSelect(product)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(product); } }}
      aria-label={`Ver detalle de ${name}`}
    >
      <div className="prod-card-img-wrap">
        {/* P3-9b: sizes ≈ ancho real de la tarjeta → 1x baja la variante chica (medium),
            2x la de 768 (medium_large). La primera fila con prioridad alta; el resto lazy. */}
        <img src={imgSrc} srcSet={imgSrcSet || undefined} sizes="(max-width: 600px) 45vw, 200px"
          loading={prioridad ? 'eager' : 'lazy'} fetchpriority={prioridad ? 'high' : undefined} alt={name}
          onError={() => { setImgSrc(PLACEHOLDER); setImgSrcSet(''); }} className="prod-card-img" />
        {isDraft && (
          <span className="prod-card-draftflag">
            {product.status === 'local' ? 'Borrador local' : 'Borrador en WooCommerce'}
          </span>
        )}
        {conCaneca && (
          <button
            type="button"
            className="prod-card-del"
            aria-label={`Eliminar ${name}`}
            title="Eliminar producto"
            onClick={(e) => { e.stopPropagation(); onDelete(product); }}
          >
            <Trash2 size={15} aria-hidden="true" />
          </button>
        )}
        {enPapelera && (
          <div className="prod-card-trash-acts">
            {onRestore && (
              <button type="button" className="prod-card-trash-btn" aria-label={`Restaurar ${name}`}
                onClick={(e) => { e.stopPropagation(); onRestore(product); }}>
                <RotateCcw size={13} aria-hidden="true" /> Restaurar
              </button>
            )}
            {onDeleteForever && (
              <button type="button" className="prod-card-trash-btn prod-card-trash-btn--danger" aria-label={`Eliminar definitivamente ${name}`}
                onClick={(e) => { e.stopPropagation(); onDeleteForever(product); }}>
                <Trash2 size={13} aria-hidden="true" /> Eliminar
              </button>
            )}
          </div>
        )}
      </div>
      <div className="prod-card-body">
        <div className="prod-card-name">{name}</div>
        {product.categories?.length > 0 && (
          <div className="prod-card-cats">
            {product.categories.slice(0, 2).map((c) => <span key={c} className="prod-cat-badge">{c}</span>)}
          </div>
        )}
        <div className="prod-card-footer">
          <span className="prod-card-price">
            {product.sale_price && product.sale_price !== product.regular_price ? (
              <>
                <span className="price-sale">${parseFloat(product.sale_price).toLocaleString('es-CO')}</span>{' '}
                <span className="price-was">${parseFloat(product.regular_price).toLocaleString('es-CO')}</span>
              </>
            ) : product.price ? `$${parseFloat(product.price).toLocaleString('es-CO')}` : <span className="price-none">—</span>}
          </span>
          <StatusBadge variant={st.variant}>{st.label}</StatusBadge>
        </div>
        {/* El plural de "variación" PIERDE la tilde: hay que alternar la palabra
            completa, no pegarle un sufijo 'es' (daría "variaciónes"). */}
        {hasVars && <span className="prod-card-varhint">{product.variations.length} {product.variations.length !== 1 ? 'variaciones' : 'variación'}</span>}
        {/* Tanda 6 (PARTE C): la misma barra pequeña de la columna Ficha. */}
        {!enPapelera && !product.isLocalDraft && <FichaBarra ficha={product.ficha} compacta />}
      </div>
    </div>
  );
}

function CatalogGrid({ products, loading = false, onSelect, onDelete = null, onRestore = null, onDeleteForever = null, selectedId = null, threshold = 5, hasFilters = false, onClearFilters }) {
  if (loading) {
    return (
      <div className="catalog-grid">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="prod-card prod-card--skel">
            <div className="prod-card-img-wrap"><span className="skeleton" style={{ width: '100%', height: '100%' }} /></div>
            <div className="prod-card-body">
              <span className="skeleton" style={{ width: '80%', height: 14 }} />
              <span className="skeleton" style={{ width: '50%', height: 12, marginTop: 8 }} />
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (!products.length) {
    return (
      <div className="catalog-grid-empty">
        <div className="catalog-grid-empty-icon">🔍</div>
        <p className="catalog-grid-empty-title">{hasFilters ? 'Sin resultados' : 'Sin productos'}</p>
        <p className="catalog-grid-empty-desc">{hasFilters ? 'Ningún producto coincide con los filtros.' : 'Cuando haya productos cargados aparecerán aquí.'}</p>
        {hasFilters && onClearFilters && (
          <button type="button" className="catalog-grid-empty-clear" onClick={onClearFilters}>Limpiar filtros</button>
        )}
      </div>
    );
  }
  return (
    <div className="catalog-grid">
      {products.map((p, i) => (
        <ProductCard key={p.id} product={p} onSelect={onSelect} onDelete={onDelete}
          onRestore={onRestore} onDeleteForever={onDeleteForever}
          selected={p.id === selectedId} threshold={threshold}
          prioridad={i < 5} />
      ))}
    </div>
  );
}

export default CatalogGrid;
